import { type TFunction } from 'react-i18next';
import { namedToOption } from 'services/helpers';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import { AUTOMATION_STATE } from 'services/Automation2Service';
import { isContact } from 'utility/fieldHelpers';
import type {
  Automation,
  AutomationVariableDataType,
  GenericOption,
  KizenNodeType,
  Step,
} from './types';
import type { AddErrorMessage, MessageDictionary } from './messageDictionary';

// default values for dateOffset dropdown

export const DateOffsetValues = {
  DATEOFFSET_ON_DAY: 'on_day',
  DATEOFFSET_ON_DAY_AND_TIME: 'on_day_and_time',
  DATEOFFSET_DAYS_BEFORE: 'days_before',
  DATEOFFSET_DAYS_AFTER: 'days_after',
} as const;

export type DateOffset = ValueOf<typeof DateOffsetValues>;

type AutomationState = Lowercase<keyof typeof AUTOMATION_STATE>;

export const isBeforeAfter = (dateOffset: DateOffset) =>
  dateOffset === DateOffsetValues.DATEOFFSET_DAYS_BEFORE ||
  dateOffset === DateOffsetValues.DATEOFFSET_DAYS_AFTER;

export const isDateAndTime = (dateOffset: DateOffset) =>
  dateOffset === DateOffsetValues.DATEOFFSET_ON_DAY_AND_TIME;

export const dateOffsetValuesTranslated = (t: TFunction) => [
  {
    label: t('On Date'),
    value: DateOffsetValues.DATEOFFSET_ON_DAY,
    filterable: false,
  },
  {
    label: t('On Date and Time'),
    value: DateOffsetValues.DATEOFFSET_ON_DAY_AND_TIME,
    filterable: true,
  },
  {
    label: t('Day(s) Before'),
    value: DateOffsetValues.DATEOFFSET_DAYS_BEFORE,
    filterable: false,
  },
  {
    label: t('Day(s) After'),
    value: DateOffsetValues.DATEOFFSET_DAYS_AFTER,
    filterable: false,
  },
];

export const unitDisplayTranslated = (t: TFunction) => ({
  minutes: {
    single: t('Minute'),
    plural: t('Minutes'),
  },
  hours: {
    single: t('Hour'),
    plural: t('Hours'),
  },
  days: {
    single: t('Day'),
    plural: t('Days'),
  },
});

export const TIMEOFFSET_SPECIFIC_TIME = 'specific_time';
export const TIMEOFFSET_SPECIFIC_AUTOMATION_TIME = 'automation_time';
export const TIMEOFFSET_FROM_FIELD = 'from_field';
export const TIMEOFFSET_HOURS_BEFORE = 'hours_before';
export const TIMEOFFSET_HOURS_AFTER = 'hours_after';
export const TIMEOFFSET_MINUTES_BEFORE = 'minutes_before';
export const TIMEOFFSET_MINUTES_AFTER = 'minutes_after';

export const onOrAroundTimeOffsetValuesTranslated = (
  isGlobalAutomation: boolean,
  t: TFunction
) =>
  [
    {
      label: t('Specific Time'),
      value: TIMEOFFSET_SPECIFIC_TIME,
    },
    !isGlobalAutomation && {
      label: t('Time from Field'),
      value: TIMEOFFSET_FROM_FIELD,
    },
    {
      label: t('Hour(s) Before'),
      value: TIMEOFFSET_HOURS_BEFORE,
    },
    {
      label: t('Hour(s) After'),
      value: TIMEOFFSET_HOURS_AFTER,
    },
    {
      label: t('Minute(s) Before'),
      value: TIMEOFFSET_MINUTES_BEFORE,
    },
    {
      label: t('Minute(s) After'),
      value: TIMEOFFSET_MINUTES_AFTER,
    },
  ].filter((v) => !!v) as GenericOption[];

export const withDelayTimeOffsetValuesTranslated = (t: TFunction) => [
  {
    label: t('Time from Agentic Workflow'),
    value: TIMEOFFSET_SPECIFIC_AUTOMATION_TIME,
    daysOnly: false,
  },
  {
    label: t('Specific Time'),
    value: TIMEOFFSET_SPECIFIC_TIME,
    daysOnly: true,
  },
  {
    label: t('Hour(s) Before'),
    value: TIMEOFFSET_HOURS_BEFORE,
    daysOnly: true,
  },
  {
    label: t('Hour(s) After'),
    value: TIMEOFFSET_HOURS_AFTER,
    daysOnly: true,
  },
  {
    label: t('Minute(s) Before'),
    value: TIMEOFFSET_MINUTES_BEFORE,
    daysOnly: true,
  },
  {
    label: t('Minute(s) After'),
    value: TIMEOFFSET_MINUTES_AFTER,
    daysOnly: true,
  },
];

/*
 ** These are recipient values
 */
export const notifyMemberViaEmailTypes = {
  NONE: 'none',
  LAST_ACTIVE: 'last_active',
  LAST_ACTIVE_ROLE: 'last_active_role',
  OWNER: 'owner',
  SPECIFIC_TEAM_MEMBERS: 'employees',
  TEAM_SELECTOR_FIELD: 'team_selector_field',
};

export type NotifyMemberViaTextTypes =
  | 'none'
  | 'last_active'
  | 'last_active_role'
  | 'owner'
  | 'employee'
  | 'team_selector_field';

export const notifyMemberViaTextTypes: Record<
  string,
  NotifyMemberViaTextTypes
> = {
  NONE: 'none',
  LAST_ACTIVE: 'last_active',
  LAST_ACTIVE_ROLE: 'last_active_role',
  OWNER: 'owner',
  SPECIFIC_TEAM_MEMBER: 'employee',
  TEAM_SELECTOR_FIELD: 'team_selector_field',
} as const;

export const fromTypeOptionsTranslated = (
  t: TFunction,
  contextIsCustomObject = true,
  addPrefixNone = false,
  isEmail = true,
  isGlobalAutomation = false
): GenericOption[] => {
  return [
    addPrefixNone && {
      label: t('None'),
      value: notifyMemberViaEmailTypes.NONE,
    },
    !isGlobalAutomation && {
      label: t('Last Active Team Member (Any Role)'),
      value: notifyMemberViaEmailTypes.LAST_ACTIVE,
    },
    !isGlobalAutomation && {
      label: t('Last Active Team Member (Specific Role)'),
      value: notifyMemberViaEmailTypes.LAST_ACTIVE_ROLE,
    },
    !isGlobalAutomation &&
      contextIsCustomObject && {
        label: t('Owner'),
        value: notifyMemberViaEmailTypes.OWNER,
      },
    {
      label: isEmail ? t('Specific Team Members') : t('Specific Team Member'),
      value: isEmail
        ? notifyMemberViaEmailTypes.SPECIFIC_TEAM_MEMBERS
        : notifyMemberViaTextTypes.SPECIFIC_TEAM_MEMBER,
    },
    !isGlobalAutomation && {
      label: t('Custom Team Selector Field'),
      value: notifyMemberViaEmailTypes.TEAM_SELECTOR_FIELD,
    },
  ].filter(Boolean) as GenericOption[];
};

export const dictionaryFromTypeOptionsTranslated = (
  t: TFunction,
  contextIsCustomObject: boolean,
  addPrefixNone: boolean,
  isEmail: boolean,
  isGlobalAutomation: boolean = false
) =>
  fromTypeOptionsTranslated(
    t,
    contextIsCustomObject,
    addPrefixNone,
    isEmail,
    isGlobalAutomation
  ).reduce(
    (acc, { label, value }: any) => ({
      ...acc,
      [value]: { value, label },
    }),
    {}
  );

/*
 ** These are email sender values
 */
export const sendRelatedContactEmailTypes = {
  LAST_ACTIVE: 'last_active',
  LAST_ACTIVE_ROLE: 'last_active_role',
  SPECIFIC_TEAM_MEMBER: 'employee',
};

export const RECEIVING_INBOX = {
  any: 'any',
  selectedIntegratedInboxes: 'selected_integrated_inboxes',
};

export const CRITERIA = {
  any: 'any_team_member',
  last: 'last_team_member',
};

type DescriptorLike = {
  name: string;
  label: string;
  displayName: string;
  description: string;
};
export const nameDescriptor = ({ name }: DescriptorLike) => name;
export const labelDescriptor = ({ label }: DescriptorLike) => label;
export const displayNameDescriptor = ({ displayName }: DescriptorLike) =>
  displayName;
export const descriptionDescriptor = ({ description }: DescriptorLike) =>
  description;

type CheckDeleted = {
  messageDictionary: MessageDictionary;
  addErrorMessage: AddErrorMessage;
  objectToCheck: any;
  descriptorCallback: any;
  itemName?: string;
  objectPath?: any;
  objectErrorTrans?: (o: any) => void;
};

export const checkDeleted = (
  messageDictionary: CheckDeleted['messageDictionary'],
  addErrorMessage: CheckDeleted['addErrorMessage'],
  objectToCheck: CheckDeleted['objectToCheck'],
  descriptorCallback: CheckDeleted['descriptorCallback'],
  itemName?: CheckDeleted['itemName'],
  objectPath?: CheckDeleted['objectPath'],
  objectErrorTrans?: CheckDeleted['objectErrorTrans']
) => {
  const errors = {};
  if (!objectToCheck && itemName) {
    addErrorMessage(
      `${messageDictionary.thereWasanErrorLoadingThe} '${itemName}'`
    );
    _set(errors, objectPath, itemName);
  }

  if (objectToCheck?.deleted) {
    const descriptorItem = descriptorCallback(objectToCheck);
    _set(errors, objectPath, objectErrorTrans?.(objectToCheck));
    addErrorMessage(
      `${messageDictionary.thereWasanErrorLoadingThe} '${
        descriptorItem ? descriptorItem : messageDictionary.associatedItem
      }'`
    );
  }
  return errors;
};

// eventualy this will replace checkDeleted

export const _checkDeleted = ({
  messageDictionary,
  addErrorMessage,
  objectToCheck,
  descriptorCallback,
  itemName = undefined,
  objectPath = 'placeholder',
  objectErrorTrans = (o) => o,
}: CheckDeleted) =>
  checkDeleted(
    messageDictionary,
    addErrorMessage,
    objectToCheck,
    descriptorCallback,
    itemName,
    objectPath,
    objectErrorTrans
  );

type checkNestedDeleted = {
  messageDictionary: MessageDictionary;
  addErrorMessage: AddErrorMessage;
  objectToCheck: any;
  descriptorCallback: any;
  itemName?: string;
  objectPath?: any;
  objectErrorTrans?: (o: any) => void;
};

export const checkNestedDeleted = (
  messageDictionary: MessageDictionary,
  addErrorMessage: AddErrorMessage,
  parentObject: any,
  objectToCheckPath: string,
  descriptorCallback: any
) =>
  (parentObject || []).forEach((object: any) => {
    checkDeleted(
      messageDictionary,
      addErrorMessage,
      object[objectToCheckPath],
      descriptorCallback
    );
  });

export const checkListDeleted = (
  messageDictionary: MessageDictionary,
  addErrorMessage: AddErrorMessage,
  parentObject: any,
  descriptorCallback: any,
  itemName: string = '',
  objectPath: string = 'undefined',
  objectErrorTrans: (o: any) => void = (o: any) => o,
  checkChildren: boolean = true
) => {
  const errors = {};

  if (itemName && !parentObject?.length) {
    addErrorMessage(
      `${messageDictionary.thereWasanErrorLoadingThe} '${itemName}'`
    );
    _set(errors, objectPath, itemName);
    return errors;
  }
  if (checkChildren) {
    const errorList = (parentObject || []).reduce((acc: any, object: any) => {
      const error = _checkDeleted({
        messageDictionary,
        addErrorMessage,
        objectToCheck: object,
        descriptorCallback,
        objectPath: 'name',
        objectErrorTrans,
      });
      if (!_isEmpty(error)) {
        acc.push(error);
      }
      return acc;
    }, []);
    if (errorList.length) {
      _set(
        errors,
        objectPath,
        errorList.flatMap(({ name }: any) => name)
      );
    }
  }

  return errors;
};

// eventualy this will replace checkListDeleted
type _CheckListDeleted = {
  messageDictionary: MessageDictionary;
  addErrorMessage: AddErrorMessage;
  parentObject: any;
  descriptorCallback: any;
  itemName: string;
  objectPath: string;
  objectErrorTrans: (o: any) => void;
  checkChildren: boolean;
};
export const _checkListDeleted = ({
  messageDictionary,
  addErrorMessage,
  parentObject,
  descriptorCallback,
  itemName = '',
  objectPath,
  objectErrorTrans = (o) => o,
  checkChildren = true,
}: _CheckListDeleted) =>
  checkListDeleted(
    messageDictionary,
    addErrorMessage,
    parentObject,
    descriptorCallback,
    itemName,
    objectPath,
    objectErrorTrans,
    checkChildren
  );
export const setDelayAmount = (delayAmount: number) => delayAmount || 0;

type ModelLike = {
  pipeline?: {
    stages: { id: string; name: string }[];
  };
};

export const validStageId = (id: string, model: ModelLike) =>
  id ? model?.pipeline?.stages.some((stage) => stage.id === id) : true;

export const getStageName = (
  id: string,
  model: ModelLike,
  messageDictionary: MessageDictionary
) => {
  if (id) {
    if (model?.pipeline?.stages) {
      const stage = model?.pipeline?.stages.find((stage) => stage.id === id);
      return namedToOption({
        id,
        name: stage?.name || messageDictionary.deleted,
      });
    }
  }
  return namedToOption({
    id,
    name: messageDictionary.anyStage,
  });
};

// this is only used for the first option
export const goalOptions = [
  {
    label: 'Never',
    value: 'never',
  },

  {
    label: 'After Delay',
    value: 'delay',
  },

  {
    label: 'On Specific Date',
    value: 'specific_datetime',
  },
] as const;

export type GoalOptions = ValueOf<typeof goalOptions>;

export const allowChange = (step: Step) =>
  step ? (step?.id || '').startsWith('new') : true;

const statusLookupForQueryFilter = {
  [AUTOMATION_STATE.ACTIVE]: AUTOMATION_STATE.ACTIVE,
  [AUTOMATION_STATE.COMPLETED]: [
    AUTOMATION_STATE.CANCELLED,
    AUTOMATION_STATE.COMPLETED,
  ],
  [AUTOMATION_STATE.PAUSED]: [
    'inactive',
    AUTOMATION_STATE.PAUSED,
    AUTOMATION_STATE.PAUSED_BY_FAILURE,
  ],
};

const getQueryFilterLink = (
  { id, customObject, type }: Automation,
  status: AutomationState
) => {
  if (type === 'global') {
    return `/agentic-workflow/${id}?executions=true&status=${status}`;
  }
  const baseLink = isContact(customObject)
    ? '/clients'
    : `/custom-objects/${customObject.id}`;

  return `${baseLink}?${new URLSearchParams({
    automations: JSON.stringify({
      type: 'automation2',
      subtype: 'automation',
      automation_id: id,
      status: statusLookupForQueryFilter[status],
    }),
  })}`;
};

export const getAutomationStatsConfig = (
  automation: Automation,
  enabled: boolean,
  t: TFunction
) => {
  return enabled
    ? [
        {
          label: t('Active'),
          value: automation.numberActive,
          href: getQueryFilterLink(automation, 'active'),
          tooltipLabel: t('View {{ status }} Records', {
            status: t('Active'),
          }),
          status: AUTOMATION_STATE.ACTIVE,
        },
        {
          label: t('Completed'),
          value: automation.numberCompleted,
          href: getQueryFilterLink(automation, 'completed'),
          tooltipLabel: t('View {{ status }} Records', {
            status: t('Completed'),
          }),
          status: AUTOMATION_STATE.COMPLETED,
        },
        {
          label: t('Paused'),
          value: automation.numberPaused,
          href: getQueryFilterLink(automation, 'paused'),
          tooltipLabel: t('View {{ status }} Records', {
            status: t('Paused'),
          }),
          status: AUTOMATION_STATE.PAUSED,
        },
      ]
    : [];
};

type GetSubtypeLabelParams =
  | {
      type: Exclude<KizenNodeType, 'condition' | 'initialize_variable'>;
      subtype?: undefined;
    }
  | {
      type: 'condition';
      subtype?: 'in_group' | 'not_in_group' | 'custom_filter' | 'llm_decision';
    }
  | {
      type: 'initialize_variable';
      subtype?: AutomationVariableDataType;
    }
  | {
      type: 'plugin_code_step';
      subtype?: string;
    };

export const getSubtypeLabel = (
  { type, subtype }: GetSubtypeLabelParams,
  t: TFunction
): string => {
  switch (type) {
    case 'archive_record':
      return t('Archive Record(s)');
    case 'assign_team_member':
      return t('Assign Team Member');
    case 'audio_transcription':
      return t('Transcribe Audio');
    case 'call_llm':
      return t('Call LLM');
    case 'change_field_value':
      return t('Change Field Value');
    case 'change_tags':
      return t('Change Tags');
    case 'code_step':
      return t('Run Code');
    case 'condition':
      switch (subtype) {
        case 'in_group':
          return t('Record In Groups');
        case 'not_in_group':
          return t('Record Not In Groups');
        case 'custom_filter':
          return t('Custom Filters');
        case 'llm_decision':
          return t('LLM Decision');
        default:
          return t('Condition');
      }
    case 'create_related_entity':
      return t('Create Record');
    case 'delay':
      return t('Wait');
    case 'delete_scheduled_activity':
      return t('Delete Scheduled Activity');
    case 'file_content_extraction':
      return t('Extract Text from File');
    case 'go_to_automation_step':
      return t('Go To Agentic Workflow Step');
    case 'goal':
      return t('Goal');
    case 'initialize_variable':
      switch (subtype) {
        case 'boolean':
          return t('Boolean');
        case 'date':
          return t('Date');
        case 'datetime':
          return t('Date/Time');
        case 'number':
          return t('Number');
        case 'phone_number':
          return t('Phone Number');
        case 'string':
          return t('String');
        case 'uuid':
          return t('UUID');
        case 'employee':
          return t('Team Member');
        case 'entity':
          return t('Object Record');
        default:
          return t('Initialize Variable');
      }
    case 'math_operator':
      return t('Calculate Value');
    case 'start_automation':
      return t('Start Agentic Workflows');
    case 'modify_automation':
      return t('Modify Agentic Workflows');
    case 'search_records':
      return t('Search Records');
    case 'modify_related_entities':
      return t('Modify Related Record');
    case 'http_request':
      return t('Send HTTP Request');
    case 'notify_member_via_email':
      return t('Email Team Members');
    case 'notify_member_via_text':
      return t('Text Team Members');
    case 'schedule_activity':
      return t('Schedule Activity');
    case 'send_related_contact_email':
      return t('Email Related Contacts');
    case 'send_related_contact_text':
      return t('Text Related Contacts');
    case 'send_text':
      return t('Send Text');
    case 'stop_execution':
      return t('Stop Execution');
    case 'exit_loop':
      return t('Exit Loop');
    case 'update_variable':
      return t('Update Variable');
    case 'plugin_code_step':
      return subtype || t('Run Code');
    case 'send_email':
      return t('Send Email');
    case 'manual':
      return t('Manual');
    case 'activity_logged':
      return t('Activity Logged');
    case 'new_entity_created':
      return t('Record Created or Unarchived');
    case 'field_updated':
      return t('Field Updated');
    case 'form_submitted':
      return t('Form Submitted');
    case 'on_or_around_date':
      return t('On or Around Date');
    case 'scheduled_activity_overdue':
      return t('Activity Past Due');
    case 'stage_updated':
      return t('Stage Updated');
    case 'survey_submitted':
      return t('Survey Submitted');
    case 'website_visited':
      return t('Website Visited');
    case 'interaction_occurred':
      return t('Interaction Occurred');
    case 'schedule':
      return t('On a Schedule');
    case 'webhook':
      return t('Webhook Received');
    case 'branch_split':
      return t('Branch Split');
    case 'contact_tag_added_removed':
      return t('Tag Added or Removed');
    case 'email_interaction':
      return t('Email Interaction');
    case 'email_link_clicked':
      return t('Email Interaction');
    case 'email_received':
      return t('Email Received from Contact');
    case 'initialize_variable_add':
      return t('Initialize Variables');
    case 'branch':
      return t('Split Branches');
    case 'merge_branches':
      return t('Merge Branches');
    case 'split_branches':
      return t('Split Branches');
    case 'loop':
      return t('For Each');
    case 'next_or_finish':
      return t('Next or Finish');
    default:
      return type;
  }
};
